---
label: Collections
category: viewers
icon: LayersIcon
keywords: [collection, sequences, group, set]
addedInVersion: "0.0.0"
---

# Sequence Collections

A sequence collection is a named group of sequences that can be viewed together,
filtered, sorted, and aligned. Collections make it easy to organize related
sequences for comparison and analysis.

## Creating a Collection

### From the File Tree

1. Select multiple sequence files in the file tree.
2. Right-click to open the context menu.
3. Choose **Group into Collection**.
4. Enter a name for the new collection.

### During Import

When importing multiple FASTA sources, you can group the imported sequences into
a collection as part of the import workflow.

## Adding Sequences

Sequences can be added to an existing collection by dragging from the file tree.
The collection must be **unlocked** to accept new sequences.

- **Drag onto the collection viewer:** drag a sequence from the file tree onto
  an open collection viewer. A blue ring appears around the viewer when the
  drop target is valid.
- **Drag onto the collection in the tree:** drag a sequence from the file tree
  directly onto a collection item in the tree.

If the collection is locked, unlock it first using the lock toggle in the
toolbar.

## Removing Sequences

### Remove a Single Sequence

1. Right-click a sequence row in the collection viewer.
2. Choose **Remove** from the context menu.
3. Confirm the removal in the dialog.

### Remove Multiple Sequences

1. Select sequences using their checkboxes. Use shift-click for range selection.
2. Click the trash icon (**Remove selected**) in the toolbar.
3. Confirm the removal in the dialog.

Removing a sequence from a collection **permanently deletes** the sequence data.
This action cannot be undone. A confirmation dialog appears before any removal
is carried out.

## Toolbar Controls

### Selection

- **Select all / clear checkbox** — toggle selection of all visible sequences.
- **Selection count** — shows how many sequences are currently selected.

### Filtering and Sorting

- **Filter by name or metadata** — type to search sequences by name, organism, description, accession, or custom metadata values.
- **Sort** — order sequences by name or length.
- **Filter by kind** — show all sequences, only nucleotide, or only amino acid.

### Actions

- **Export** — copy or download sequences as FASTA. Options include copying to
  clipboard, downloading as a single multi-FASTA file, or downloading each
  sequence as a separate file in a zip archive.
- **Concatenate** — merge two or more selected sequences of the same kind into a
  single sequence.

### View Options

- **GC content** — toggle a GC content track below nucleotide sequences. Adjust
  the calculation window size with the slider.
- **Snap to rows** — align scrolling to sequence row boundaries for easier
  visual scanning.
- **Lock** — prevent modifications to the collection. When locked, sequences
  cannot be added, removed, or reordered.

## Alignment

Collections with two or more sequences of the same kind can be aligned. Click
the **Alignment** tab in the viewer header to start an alignment.

The alignment tab is disabled when the collection has fewer than two sequences or
contains only mixed sequence types. Hover over the disabled tab to see why it is
unavailable.

## Tips

- **Lock** a collection after assembling it to prevent accidental modifications.
- Use the **kind filter** to focus on nucleotide or amino acid sequences when
  working with mixed collections.
- **Snap to rows** helps when scanning through many sequences visually.
- Use **shift-click** to quickly select a range of sequences for batch
  operations.
